import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Plus, Search, Loader2 } from "lucide-react";
import { z } from "zod";
import { AppShell } from "@/components/app-shell/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Card";
import { StatusDot } from "@/components/common/StatusDot";
import { api } from "@/lib/api";
import { fmtTs } from "@/lib/format";

const searchSchema = z.object({
  site: z.string().optional(),
});

export const Route = createFileRoute("/forms/")({
  validateSearch: searchSchema,
  component: FormsPage,
});

function FormsPage() {
  const { site: siteFilter } = useSearch({ from: "/forms/" });
  const [search, setSearch] = useState("");

  const sitesQ = useQuery({ queryKey: ["sites"], queryFn: () => api.listSites() });
  const formsQ = useQuery({ queryKey: ["forms"], queryFn: () => api.listForms() });

  const sites = sitesQ.data?.sites ?? [];
  const forms = formsQ.data?.forms ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return forms.filter((f) => {
      if (siteFilter && f.siteKey !== siteFilter) return false;
      if (
        q &&
        !f.name.toLowerCase().includes(q) &&
        !f.formKey.toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [forms, siteFilter, search]);

  const newFormBtnClasses =
    "inline-flex items-center gap-2 rounded-md bg-ember px-3 py-1.5 text-xs font-semibold text-background ring-1 ring-ember/40 transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <AppShell title="Forms">
      <PageHeader
        eyebrow="forms"
        title="Forms"
        description="Configured form endpoints across all sites. Each form is a sealed delivery contract with its own rate limits, recipients, and Turnstile keys."
        actions={
          sites.length === 0 ? (
            <button
              type="button"
              disabled
              className={newFormBtnClasses}
              title="Create a site first"
            >
              <Plus className="h-3.5 w-3.5" /> New form
            </button>
          ) : (
            <Link
              to="/forms/new"
              search={{ site: siteFilter ?? undefined }}
              className={newFormBtnClasses}
            >
              <Plus className="h-3.5 w-3.5" /> New form
            </Link>
          )
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder="Search by form name or key…"
            className="h-9 w-full rounded-md border border-border bg-surface/40 pl-9 pr-3 text-xs text-zinc-200 placeholder:text-zinc-500 focus:border-ember/60 focus:outline-none focus:ring-2 focus:ring-ember/20"
          />
        </div>
        <Link
          to="/forms"
          search={{ site: undefined } as never}
          className={`rounded-md border border-border bg-surface/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${
            siteFilter ? "text-ember hover:bg-surface" : "text-zinc-500"
          }`}
        >
          {siteFilter ? `site = ${siteFilter} · clear ✕` : "All sites"}
        </Link>
      </div>

      <Panel className="overflow-hidden">
        <div className="overflow-x-auto">
          {formsQ.isLoading ? (
            <div className="flex items-center gap-2 px-6 py-8 text-xs text-zinc-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> loading forms…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-12 text-center text-xs text-zinc-500">
              {forms.length === 0
                ? "No forms yet — create one to start receiving submissions."
                : "No forms match this filter."}
            </div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="px-6 py-3 font-medium">Form</th>
                  <th className="px-6 py-3 font-medium">Site</th>
                  <th className="px-6 py-3 font-medium">Recipients</th>
                  <th className="px-6 py-3 font-medium">Origins</th>
                  <th className="px-6 py-3 font-medium">Rate limit</th>
                  <th className="px-6 py-3 font-medium">Updated</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => {
                  const site = sites.find((s) => s.siteKey === f.siteKey);
                  const rpm = f.limits?.perIpPerMin ?? null;
                  return (
                    <tr
                      key={f.formKey}
                      className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.03]"
                    >
                      <td className="px-6 py-4">
                        <Link
                          to="/forms/$formKey"
                          params={{ formKey: f.formKey }}
                          className="block"
                        >
                          <div className="text-sm font-medium text-zinc-100 hover:text-ember">
                            {f.name}
                          </div>
                          <div className="mt-0.5 font-mono text-[10px] text-zinc-500">
                            {f.formKey.slice(0, 16)}…
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-300">
                        {site?.name ?? f.siteKey}
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-zinc-300">
                        {f.recipients.length}
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-zinc-300">
                        {f.allowedOrigins.length}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-zinc-700">
                            <div
                              className="h-full bg-success"
                              style={{
                                width: `${Math.min(100, ((rpm ?? 3) / 30) * 100)}%`,
                              }}
                            />
                          </div>
                          <span className="font-mono text-[10px] text-zinc-500">
                            {rpm ?? "default"}/min
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-mono text-[11px] text-zinc-500">
                        {fmtTs(f.updatedAt)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-zinc-300">
                          <StatusDot status={f.enabled ? "healthy" : "neutral"} />
                          {f.enabled ? "enabled" : "disabled"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Panel>
    </AppShell>
  );
}
